<script setup>
import {computed} from "vue";
import {
    mdiAccount,
    mdiBrightness3,
    mdiBrightness7,
    mdiMenu,
    mdiVolumeHigh,
    mdiVolumeOff,
    mdiWindowClose
} from "@mdi/js";
import HpoMemberRoleSelect from "./HpoMemberRoleSelect.vue";
import {currentUser} from "@/infrastructure/user";
import {audioEnabled, toggleAudio} from "@/audio";
import {wakeLockSupported, wakeLockActive, toggleWakeLock} from "@/wakeLock";
import {HpoRole} from "@/components/hpo/choices";

const props = defineProps({
    drawer: {
        type: Boolean,
        required: true
    },
    hpoMember: {
        type: Object,
        required: false,
        default: undefined
    },
})
const emit = defineEmits(['update:drawer'])


const appBarNavIcon = computed(() => {
    if (props.drawer) {
        return mdiWindowClose
    }
    return mdiMenu
})

const audioIcon = computed(() => {
    if (audioEnabled.value) {
        return mdiVolumeHigh
    } else {
        return mdiVolumeOff
    }
})

const audioTooltip = computed(() => {
    if (audioEnabled.value) {
        return pgettext('tooltip', 'Mute audio');
    } else {
        return pgettext('tooltip', 'Unmute audio');
    }
})

const wakeLockIcon = computed(() => {
    if (wakeLockActive.value) {
        return mdiBrightness7;
    } else {
        return mdiBrightness3;
    }
})

const wakeLockTooltip = computed(() => {
    if (wakeLockActive.value) {
        return pgettext('tooltip', 'Disable screen wake lock');
    } else {
        return pgettext('tooltip', 'Enable screen wake lock');
    }
})

const userName = computed(() => {
    if (props.hpoMember) {
        return props.hpoMember.name;
    }
    if (currentUser.lastName || currentUser.firstName) {
        return `${currentUser.lastName} ${currentUser.firstName}`;
    }
    return null;
});
</script>

<template>
    <v-app-bar dense class="d-flex justify-space-between" rounded>

        <v-app-bar-nav-icon :icon="appBarNavIcon"
                            @click="emit('update:drawer', !drawer)"></v-app-bar-nav-icon>


        <v-app-bar-title>
            <div v-if="hpoMember?.role" class="d-flex ga-4 align-center">
                <HpoMemberRoleSelect v-if="currentUser.isSuperuser" :hpoMember="hpoMember"/>
                <slot></slot>
            </div>
        </v-app-bar-title>

        <template #append>
            <v-tooltip v-if="wakeLockSupported" :text="wakeLockTooltip" location="start">
                <template #activator="{ props }">
                    <v-btn icon v-bind="props" @click="toggleWakeLock()" class="mr-2">
                        <v-icon :icon="wakeLockIcon" />
                    </v-btn>
                </template>
            </v-tooltip>
            <v-tooltip :text="audioTooltip" location="start">
                <template #activator="{ props }">
                    <v-btn icon v-bind="props" @click="toggleAudio()" class="mr-2">
                        <v-icon :icon="audioIcon" />
                    </v-btn>
                </template>
            </v-tooltip>

            <v-chip :prepend-icon="mdiAccount" class="d-none d-sm-flex mr-4 mr-lg-6" rounded>
                <strong>{{ userName }}</strong>
            </v-chip>
        </template>

    </v-app-bar>
</template>